import Head from 'next/head'
import Link from 'next/link'
import React from 'react'

import { SITE_URL } from '@goodfellas/environment'

import {
  LEGAL_COMPANY,
  LEGAL_DOCS,
  LEGAL_UPDATED_AT,
  LEGAL_UPDATED_AT_LABEL,
  LEGAL_VERSION,
  LegalDocKey,
  LegalLang,
} from '../../constants/legal'
import { LegalTocItem } from '../../functions/legalContent'
import s from './legal.module.css'

const BASE = SITE_URL.replace(/\/+$/, '')

/**
 * Strings do chrome (não do documento). Os documentos legais existem só em
 * PT (autoritativo) e EN (tradução de trabalho) — locales restantes recebem
 * a página EN —, então não passam pelos catálogos i18n de 11 idiomas.
 */
const CHROME = {
  pt: {
    toc: 'Sumário',
    contact: 'Contato',
    privacy: 'Privacidade/DPO',
    headOffice: 'Sede',
    version: (updated: string) =>
      `Versão ${LEGAL_VERSION} · atualizado em ${updated}`,
    switchLabel: 'English',
    otherDoc: { terms: 'Política de Privacidade', privacy: 'Termos de Uso' },
  },
  en: {
    toc: 'Contents',
    contact: 'Contact',
    privacy: 'Privacy/DPO',
    headOffice: 'Registered office',
    version: (updated: string) =>
      `Version ${LEGAL_VERSION} · updated ${updated}`,
    switchLabel: 'Português',
    otherDoc: { terms: 'Privacy Policy', privacy: 'Terms of Use' },
  },
} as const

export interface LegalPageProps {
  docKey: LegalDocKey
  lang: LegalLang
  html: string
  toc: LegalTocItem[]
}

/**
 * Página legal estática (handoff "Páginas Legais" v4.0): header com marca e
 * troca de idioma, bloco de metadados (razão social/CNPJ/versão), TOC com
 * âncoras dos H2 e o Markdown renderizado. PT em /termos e /privacidade; EN
 * nas URLs bonitas /en/terms-of-use e /en/privacy-policy (rewrites no
 * next.config.js). O noindex de stage é o header global do next.config.js.
 */
export function LegalPage({ docKey, lang, html, toc }: LegalPageProps) {
  const doc = LEGAL_DOCS[docKey]
  const other = LEGAL_DOCS[docKey === 'terms' ? 'privacy' : 'terms']
  const chrome = CHROME[lang]
  const updated = LEGAL_UPDATED_AT_LABEL[lang]
  const canonical = `${BASE}${doc.path[lang]}`

  const switchTarget = lang === 'pt' ? 'en' : 'pt'

  return (
    <>
      <Head>
        <title>{`${doc.title[lang]} | Goodfellas`}</title>
        <meta name="description" content={doc.description[lang]} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="pt-BR" href={`${BASE}${doc.path.pt}`} />
        <link rel="alternate" hrefLang="en" href={`${BASE}${doc.path.en}`} />
        {/* x-default aponta para o PT: é o idioma autoritativo do documento. */}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${BASE}${doc.path.pt}`}
        />
      </Head>
      <main className={s.page} lang={lang === 'pt' ? 'pt-BR' : 'en'} dir="ltr">
        <header className={s.top}>
          {/* Sem locale explícito: preserva o locale atual do router (um
              usuário em /es/termos volta para a home /es). */}
          <Link href="/" className={s.brand}>
            Goodfellas
          </Link>
          <nav aria-label="Idioma / Language">
            <Link
              href={doc.path[switchTarget]}
              locale={false}
              className={s.langLink}
              lang={switchTarget === 'pt' ? 'pt-BR' : 'en'}
            >
              {chrome.switchLabel}
            </Link>
          </nav>
        </header>

        {lang === 'en' && (
          <p className={s.notice}>
            This is a working translation. The{' '}
            <Link href={doc.path.pt} locale={false} lang="pt-BR">
              Brazilian Portuguese version
            </Link>{' '}
            prevails in case of conflict.
          </p>
        )}

        <div className={s.head}>
          <h1 className={s.title}>{doc.title[lang]}</h1>
          <section className={s.meta}>
            <p>
              <strong>{LEGAL_COMPANY.legalName}</strong>
              {docKey === 'terms' && ` (${LEGAL_COMPANY.tradeName})`} · CNPJ{' '}
              {LEGAL_COMPANY.cnpj}
            </p>
            <p>
              {chrome.headOffice}: {LEGAL_COMPANY.address[lang]}
            </p>
            <p>
              {chrome.contact}:{' '}
              <a href={`mailto:${LEGAL_COMPANY.contactEmail}`}>
                {LEGAL_COMPANY.contactEmail}
              </a>{' '}
              · {chrome.privacy}:{' '}
              <a href={`mailto:${LEGAL_COMPANY.privacyEmail}`}>
                {LEGAL_COMPANY.privacyEmail}
              </a>
            </p>
            <p className={s.version}>
              {chrome.version(updated)} · {doc.scope[lang]}
            </p>
          </section>
        </div>

        <div className={s.grid}>
          <aside className={s.toc}>
            {/* <details> aberto: acordeão de verdade só no mobile; no
                desktop o CSS trava o summary e o box vira coluna sticky. */}
            <details className={s.tocBox} open>
              <summary>{chrome.toc}</summary>
              <nav aria-label={chrome.toc}>
                <ol>
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`}>{item.label}</a>
                    </li>
                  ))}
                </ol>
              </nav>
            </details>
          </aside>

          <article
            className={s.body}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>

        <footer className={s.foot}>
          <p>
            {LEGAL_COMPANY.legalName} · CNPJ {LEGAL_COMPANY.cnpj} ·{' '}
            {LEGAL_COMPANY.address[lang]}
          </p>
          <p>
            <time dateTime={LEGAL_UPDATED_AT}>{chrome.version(updated)}</time>
          </p>
          <p className={s.footLinks}>
            <Link href={other.path[lang]} locale={false}>
              {chrome.otherDoc[docKey]}
            </Link>
            <Link
              href={doc.path[switchTarget]}
              locale={false}
              lang={switchTarget === 'pt' ? 'pt-BR' : 'en'}
            >
              {chrome.switchLabel}
            </Link>
          </p>
        </footer>
      </main>
    </>
  )
}
